import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  checklistDefinitionSchema,
  type ChecklistDefinition,
  type ChecklistTemplate,
} from '@shared/checklists/stack.ts';
import { checklistStackApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature, PlanRequiredPage } from '../components/plan-gate.tsx';
import { Alert, Button, LoadingBlock } from '../components/ui.tsx';
import { ChecklistDefinitionEditor, newChecklistDefinition } from '../components/checklist-definition-editor.tsx';
import { useChecklistEditScope } from '../components/checklist-edit-scope.ts';
import { useLocalizedContent } from '../i18n/localized.ts';

export default function ChecklistTemplatesPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const scopeGuard = useChecklistEditScope('templates:write');
  const localized = useLocalizedContent();
  const scheduling = usePlanFeature('scheduling');
  const photoAllowed = usePlanFeature('checklist_reports');
  const queryClient = useQueryClient();
  const request = useRef(crypto.randomUUID());
  const [editing, setEditing] = useState<ChecklistTemplate | 'new' | null>(null);
  const [value, setValue] = useState<ChecklistDefinition>(newChecklistDefinition);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);
  const templates = useQuery({
    queryKey: ['checklist-templates'],
    queryFn: checklistStackApi.templates,
    enabled: scheduling,
  });
  const save = useMutation({
    mutationFn: async () => {
      if (!scopeGuard.allowed) throw new Error('The checklist edit scope changed.');
      const editScope = scopeGuard.stamp;
      const result = await checklistStackApi.saveTemplate(editing === 'new' ? null : (editing?.id ?? null), {
        requestId: request.current,
        ...(editing && editing !== 'new' ? { version: editing.version } : {}),
        definition: value,
      });
      return { ...result, editScope };
    },
    onSuccess: async (result) => {
      if (!scopeGuard.isCurrent(result.editScope)) return;
      setSaved(result.refreshed);
      setEditing(null);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['checklist-templates'] });
    },
    onError: (caught) =>
      scopeGuard.isCurrent() &&
      setError(
        t(
          caught instanceof ApiRequestError && caught.status === 409
            ? 'checklistStack.stale'
            : 'checklistStack.saveError',
        ),
      ),
  });
  // Refused while a property checklist or a schedule weekday uses the
  // template: the office takes it off there first, so no crew is handed a
  // different list without anyone choosing it.
  const remove = useMutation({
    mutationFn: (id: string) => checklistStackApi.deleteTemplate(id),
    onMutate: () => {
      setError(null);
      setSaved(null);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['checklist-templates'] });
    },
    onError: (caught) =>
      setError(
        t(
          caught instanceof ApiRequestError && caught.code === 'CHECKLIST_TEMPLATE_IN_USE'
            ? 'checklistStack.deleteInUse'
            : 'checklistStack.deleteError',
        ),
      ),
  });
  if (!scheduling) return <PlanRequiredPage feature="scheduling" title={t('checklistStack.library')} />;
  return (
    <WorkPage
      title={t('checklistStack.library')}
      description={t('checklistStack.libraryHint')}
      actions={
        <Button
          disabled={!can('templates:write') || save.isPending}
          onClick={() => {
            scopeGuard.capture();
            setEditing('new');
            setValue(newChecklistDefinition());
            request.current = crypto.randomUUID();
            setError(null);
          }}
        >
          {t('checklistStack.newTemplate')}
        </Button>
      }
    >
      <Link to="/customers" className="text-sm underline">
        {t('nav.customers')}
      </Link>
      {error && <Alert tone="error">{error}</Alert>}
      {saved !== null && <Alert tone="success">{t('checklistStack.refreshed', { count: saved })}</Alert>}
      {templates.isLoading ? (
        <LoadingBlock />
      ) : templates.isError ? (
        <Alert tone="error">
          {t('checklistStack.loadError')}
          <Button className="mt-3" onClick={() => void templates.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}
      {!scopeGuard.sameScope && <Alert tone="error">{t('checklistStack.stale')}</Alert>}
      {editing && scopeGuard.sameScope && (
        <form
          className="card space-y-4 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!scopeGuard.allowed) return;
            if (!checklistDefinitionSchema.safeParse(value).success) {
              setError(t('checklistStack.invalid'));
              return;
            }
            save.mutate();
          }}
        >
          <ChecklistDefinitionEditor
            value={value}
            photoAllowed={photoAllowed}
            disabled={save.isPending || !scopeGuard.allowed}
            onChange={(next) => {
              setValue(next);
              request.current = crypto.randomUUID();
            }}
          />
          <Alert tone="info">{t('checklistStack.refreshHint')}</Alert>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" busy={save.isPending} disabled={!scopeGuard.allowed}>
              {t('common.save')}
            </Button>
            <Button disabled={save.isPending || !scopeGuard.allowed} onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
      <WorkSection title={t('checklistStack.template')}>
        {!templates.isLoading && !templates.isError && templates.data?.templates.length === 0 && (
          <WorkEmpty title={t('checklistStack.library')}>
            <p>{t('checklistStack.libraryHint')}</p>
          </WorkEmpty>
        )}
        <ul className="grid gap-3 sm:grid-cols-2">
          {templates.data?.templates.map((template) => (
            <li key={template.id} className="card space-y-2 p-4">
              <h2 className="font-semibold">
                {localized.text(template.definition.name, template.definition.nameTranslations)}
              </h2>
              <p className="text-sm">
                {t(`checklistStack.kinds.${template.definition.kind}`)} ·{' '}
                {template.definition.sections.reduce((count, section) => count + section.tasks.length, 0)}{' '}
                {t('checklistStack.tasks')}
              </p>
              <Button
                disabled={!can('templates:write') || save.isPending}
                onClick={() => {
                  scopeGuard.capture();
                  setEditing(template);
                  setValue(structuredClone(template.definition));
                  request.current = crypto.randomUUID();
                  setError(null);
                }}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="ghost"
                className="ml-2"
                disabled={!can('templates:write') || save.isPending}
                busy={remove.isPending && remove.variables === template.id}
                onClick={() => {
                  if (window.confirm(t('checklistStack.confirmDelete'))) remove.mutate(template.id);
                }}
              >
                {t('checklistStack.deleteTemplate')}
              </Button>
            </li>
          ))}
        </ul>
      </WorkSection>
    </WorkPage>
  );
}
